import React, { useEffect, useMemo, useState } from 'react';
import { Layers, Cloud } from 'lucide-react';
import { Card } from '../Card';
import * as api from '../../../services/api';

/**
 * ProductCatalogPanel — Admin Center "Catalog".
 *
 * Read-only view of the product catalog (Mockup 2): the edition plans and their modules (derived
 * from the platform's edition/module model), plus the cloud-marketplace listing matrix. Data comes
 * from GET /api/v1/admin/product-catalog (Platform Admin).
 */
const CLOUDS = ['GCP', 'AWS', 'AZURE'];
const CLOUD_LABEL = { GCP: 'Google Cloud', AWS: 'AWS', AZURE: 'Azure' };

const statusBadge = (status) => {
  if (status === 'LISTED') {
    return { label: 'Listed', cls: 'text-[#0F6E56] bg-[#E1F5EE]' };
  }
  if (status === 'CATALOG_READY') {
    return { label: 'Catalog-ready', cls: 'text-[#854F0B] bg-[#FAEEDA]' };
  }
  return { label: 'Not listed', cls: 'text-v2-ink-muted bg-v2-muted' };
};

export default function ProductCatalogPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.getProductCatalog();
        const d = res && res.data ? res.data : res;
        if (res && res.ok === false) throw new Error(res.error || 'Failed to load catalog');
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Module key → display name, for chips.
  const moduleName = useMemo(() => {
    const m = {};
    (data?.moduleSkus || []).forEach((s) => { m[s.key] = s.displayName; });
    return m;
  }, [data]);

  // Marketplace listings pivoted by SKU id → { cloud: status }.
  const listingBySku = useMemo(() => {
    const m = {};
    (data?.marketplace || []).forEach((l) => {
      (m[l.skuId] = m[l.skuId] || {})[l.cloud] = l.status;
    });
    return m;
  }, [data]);

  const plans = data?.plans || [];

  return (
    <div style={{ padding: '20px 28px 32px' }}>
      <div className="mb-4 text-[12px] text-v2-ink-secondary leading-relaxed">
        Editions and their modules, and how each edition is listed on the cloud marketplaces.
        Plans and modules are derived from the platform’s edition model, so they always match what’s
        entitled and enforced.
      </div>

      {loading && <div className="text-v2-ink-muted text-[12px]">Loading catalog…</div>}
      {error && (
        <div className="text-[12px] text-v2-danger border border-v2-border-subtle rounded-v2-sm px-3 py-3">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Editions & modules */}
          <div className="flex items-center gap-2 mb-2">
            <Layers size={14} className="text-v2-action-strong" />
            <div className="text-[13px] font-semibold text-v2-ink">Editions</div>
          </div>
          <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            {plans.map((p) => (
              <Card key={p.skuId} padding="sm">
                <Card.Body>
                  <div className="text-[12.5px] font-semibold text-v2-ink">{p.displayName}</div>
                  <div className="text-[10.5px] text-v2-ink-muted font-mono mb-2">{p.skuId}</div>
                  <div className="flex flex-wrap gap-1">
                    {(p.modules || []).map((k) => (
                      <span key={k} className="text-[10.5px] text-v2-ink-secondary bg-v2-muted rounded-v2-sm px-1.5 py-0.5">
                        {moduleName[k] || k}
                      </span>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>

          {/* Cloud marketplace listings */}
          <div className="flex items-center gap-2 mb-2">
            <Cloud size={14} className="text-v2-action-strong" />
            <div className="text-[13px] font-semibold text-v2-ink">Cloud marketplace listings</div>
          </div>
          <Card padding="sm">
            <Card.Body>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider">
                    <th className="text-left font-semibold py-1">Edition (SKU)</th>
                    {CLOUDS.map((c) => (
                      <th key={c} className="text-left font-semibold py-1 pl-3">{CLOUD_LABEL[c]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => (
                    <tr key={p.skuId} className="border-t border-v2-border-subtle align-top">
                      <td className="py-2 text-v2-ink">
                        {p.displayName}
                        <div className="text-[10px] text-v2-ink-muted font-mono">{p.skuId}</div>
                      </td>
                      {CLOUDS.map((c) => {
                        const b = statusBadge((listingBySku[p.skuId] || {})[c]);
                        return (
                          <td key={c} className="py-2 pl-3">
                            <span className={`text-[10.5px] px-2 py-0.5 rounded-v2-sm ${b.cls}`}>{b.label}</span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-[10.5px] text-v2-ink-muted mt-2">
                Google Cloud is listed first (list plus private offers). AWS and Azure are catalog-ready;
                integrated metering is handled per marketplace when a listing is published.
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
}
